<template>
  <section class="member-limit-list" :aria-busy="loading">
    <h3>개별 설정 회원 목록</h3>
    <p class="list-help">카지노·슬롯 중 하나라도 개별 한도가 설정된 회원입니다. 무제한(0) 설정도 포함됩니다.</p>
    <form @submit.prevent="search">
      <fieldset :disabled="loading" class="list-search">
        <label>구분
          <select v-model="filters.category">
            <option value="">전체</option>
            <option value="casino">카지노</option>
            <option value="slot">슬롯</option>
          </select>
        </label>
        <label>회원 ID <input v-model="filters.memId" type="text" autocomplete="off" placeholder="정확히 일치하는 ID" /></label>
        <button type="submit" class="list-button">조회</button>
      </fieldset>
    </form>
    <p v-if="loading" role="status">회원 목록을 불러오는 중입니다.</p>
    <div v-else-if="error" role="alert" class="list-error">
      {{ error }} <button type="button" class="list-button" @click="load(page)">다시 불러오기</button>
    </div>
    <template v-else>
      <p class="list-help" role="status">전체 {{ pageInfo.totalCount }}명 · {{ page }} / {{ pageInfo.totalPages || 1 }} 페이지</p>
      <div class="table-wrapper">
        <table class="mainTable">
          <caption>0은 무제한이며 ‘공통값 사용’은 해당 항목의 사이트 공통 한도를 따릅니다.</caption>
          <thead><tr>
            <th scope="col">회원 ID</th><th scope="col">구분</th>
            <th scope="col">개별 최대 베팅금</th><th scope="col">개별 최대 당첨금</th>
            <th scope="col">공통 최대 베팅금</th><th scope="col">공통 최대 당첨금</th>
            <th scope="col">최종 최대 베팅금</th><th scope="col">최종 최대 당첨금</th>
            <th scope="col">한도 설정</th>
          </tr></thead>
          <tbody>
            <template v-for="row in rows" :key="row.memId">
              <tr v-for="(limit, index) in row.limits" :key="limit.category">
                <td v-if="index === 0" :rowspan="row.limits.length">{{ row.memId }}</td>
                <td>{{ limit.category === 'casino' ? '카지노' : '슬롯' }}</td>
                <td>{{ limit.memberSetting?.betAmtMax == null ? '공통값 사용' : displayAmount(limit.memberSetting.betAmtMax) }}</td>
                <td>{{ limit.memberSetting?.betWinAmtMax == null ? '공통값 사용' : displayAmount(limit.memberSetting.betWinAmtMax) }}</td>
                <td>{{ displayAmount(limit.siteSetting?.betAmtMax) }}</td>
                <td>{{ displayAmount(limit.siteSetting?.betWinAmtMax) }}</td>
                <td>{{ displayAmount(limit.effective.betAmtMax) }}<p class="list-help">{{ sourceName(limit.effective.source?.betAmtMax) }}</p></td>
                <td>{{ displayAmount(limit.effective.betWinAmtMax) }}<p class="list-help">{{ sourceName(limit.effective.source?.betWinAmtMax) }}</p></td>
                <td v-if="index === 0" :rowspan="row.limits.length"><button type="button" class="list-button" :aria-label="row.memId + ' 한도 확인·수정'" @click="$emit('open-member', row.memId)">한도 확인·수정</button></td>
              </tr>
            </template>
            <tr v-if="!rows.length"><td colspan="9">조건에 맞는 개별 설정 회원이 없습니다.</td></tr>
          </tbody>
        </table>
      </div>
      <pagination v-if="pageInfo.totalCount > 0" :page-num="page" :page-size="pageInfo.count_per_list" :total-count="pageInfo.totalCount" @go-page="load" />
    </template>
  </section>
</template>

<script>
import { getMemberBetLimitList } from '@/api/setting'
import Pagination from '@/components/common/Pagination'

export default {
  name: 'MemberBetLimitList',
  components: { Pagination },
  emits: ['open-member'],
  data () {
    return { filters: { category: '', memId: '' }, query: {}, rows: [], page: 1, pageInfo: { page: 1, count_per_list: 20, totalCount: 0, totalPages: 0 }, loading: false, error: '' }
  },
  methods: {
    displayAmount (value) {
      if (value == null) return '미설정'
      if (String(value) === '0') return '무제한'
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '원'
    },
    sourceName (source) { return { SITE: '사이트 공통', MEMBER: '회원 개별', UNSET: '미설정' }[source] || '미설정' },
    search () {
      if (this.loading) return
      this.query = {}
      if (this.filters.category) this.query.category = this.filters.category
      const memId = this.filters.memId.trim()
      if (memId) this.query.memId = memId
      return this.load(1)
    },
    async load (page = 1) {
      if (this.loading || !Number.isInteger(page) || page < 1 || (page > 1 && page > this.pageInfo.totalPages)) return
      this.loading = true
      this.error = ''
      this.rows = []
      this.page = page
      try {
        const response = await getMemberBetLimitList({ ...this.query, page, count_per_list: 20 })
        if (response?.resultCode !== '0') {
          const message = { '1000': '회원 한도 목록 조회 권한이 없습니다.', TK999: '로그인 정보가 변경되었습니다. 다시 로그인해주세요.' }[response?.resultCode]
          throw new Error(message || response?.resultMessage || '회원 한도 목록을 조회하지 못했습니다.')
        }
        const list = response.data?.list
        const pageInfo = response.data?.pageInfo
        const validAmount = value => value === null || (typeof value === 'string' && /^\d+$/.test(value))
        const validSetting = setting => setting == null || ['betAmtMax', 'betWinAmtMax'].every(key => validAmount(setting[key]))
        if (!Array.isArray(list) || list.some(row => typeof row?.memId !== 'string' || !row.memId.trim() || !Array.isArray(row.limits) || !row.limits.length || row.limits.some(limit =>
          !['casino', 'slot'].includes(limit?.category) || !limit.effective || !validSetting(limit.effective) || !validSetting(limit.siteSetting) || !validSetting(limit.memberSetting)))) {
          throw new Error('회원 한도 목록 응답을 확인하지 못했습니다. 다시 불러와주세요.')
        }
        if (!pageInfo || pageInfo.page !== page || pageInfo.count_per_list !== 20 || !Number.isInteger(pageInfo.totalCount) || pageInfo.totalCount < 0 || !Number.isInteger(pageInfo.totalPages) || pageInfo.totalPages < 0) {
          throw new Error('회원 목록 페이지 정보를 확인하지 못했습니다. 다시 불러와주세요.')
        }
        this.rows = list
        this.pageInfo = pageInfo
      } catch (error) {
        this.error = [401, 422].includes(error.response?.status) || error.message === 'No token'
          ? '로그인이 만료되었습니다. 다시 로그인해주세요.'
          : error.response || error.code ? '회원 목록을 불러오지 못했습니다. 연결 상태를 확인하고 다시 시도해주세요.' : error.message || '회원 목록 조회 중 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    }
  },
  created () { this.load() }
}
</script>

<style scoped>
.member-limit-list {margin: 18px 0;padding: 18px;background: #1f2937;border: 1px solid #313a49;border-radius: 5px;color: #d1d5db;}
h3 {font-size: 16px;margin-bottom: 10px;}
.list-help, caption {font-size: 12px;line-height: 1.6;margin: 10px 0;color: #cbd5e1;text-align: left;}
.list-search {display: flex;flex-wrap: wrap;align-items: center;gap: 14px;margin: 18px 0;border: 0;padding: 0;}
.list-search label {display: flex;align-items: center;gap: 8px;}
input, select {padding: 8px;border: 1px solid #64748b;background: #111827;color: #fff;}
.list-button {padding: 8px 12px;background: #374151;color: #fff;border-radius: 4px;cursor: pointer;font: inherit;}
.list-button:disabled, fieldset:disabled {opacity: .5;}
.list-button:disabled {cursor: default;}
.list-button:focus-visible, input:focus-visible, select:focus-visible {outline: 2px solid #93c5fd;outline-offset: 2px;}
.list-error {color: #fca5a5;line-height: 1.6;}
.table-wrapper {overflow-x: auto;}
.mainTable {min-width: 1100px;}
</style>
